import { getChartInsideLabelColor } from '@digital-twins/deck-ui/foundations/chart-colors'

import { pickCategoricalChartPalette } from '@/lib/charts/chartjs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { assetPreviewPlays, AssetAxisPreview } from '@/features/studies/report/utils/testedAssets'

export interface LaneMediaSegment {
  label: string
  values: Array<number>
}

export interface LaneMediaRow {
  id: string
  label: string
  preview: AssetAxisPreview
}

interface LaneMediaBarsProps {
  rows: Array<LaneMediaRow>
  segments: Array<LaneMediaSegment>
}

const AXIS = ['0%', '20%', '40%', '60%', '80%', '100%']

/** Same cutoff as the deck's stacked segment bars: a thinner slice cannot hold the caption. */
const INSIDE_LABEL_MIN = 8

function formatShareLabel(value: number): string {
  const rounded = Math.round(value)
  const display = Math.abs(value - rounded) < 0.05 ? `${rounded}` : value.toFixed(1)
  return `${display}%`
}

function PreviewThumb(props: { row: LaneMediaRow }) {
  const { row } = props
  const plays = assetPreviewPlays(row.preview)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={row.label}
          data-testid={`lane-media-thumb-${row.id}`}
          className="relative block h-9 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-muted"
        >
          {plays ? (
            <video
              src={row.preview.url}
              muted
              playsInline
              preload="metadata"
              data-testid={`lane-media-video-${row.id}`}
              className="h-full w-full object-cover"
            />
          ) : (
            <img src={row.preview.url} alt="" className="h-full w-full object-cover" />
          )}
          {row.preview.kind === 'video' ? (
            <span
              data-testid={`lane-media-play-${row.id}`}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <span className="block h-0 w-0 border-y-[4px] border-l-[6px] border-y-transparent border-l-white drop-shadow" />
            </span>
          ) : null}
        </span>
      </TooltipTrigger>
      <TooltipContent>{row.label}</TooltipContent>
    </Tooltip>
  )
}

export function LaneMediaBars(props: LaneMediaBarsProps) {
  const { rows, segments } = props

  const colors = pickCategoricalChartPalette(segments.length)

  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="lane-media-chart" className="flex flex-col gap-3">
        <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-x-3 gap-y-3">
          {rows.map((row, rowIndex) => (
            <div key={row.id} className="contents">
              <PreviewThumb row={row} />
              <div
                className="flex h-6 min-w-0 overflow-hidden rounded-sm bg-muted/40"
                data-testid={`lane-media-bar-${row.id}`}
              >
                {segments.map((segment, segmentIndex) => {
                  const value = segment.values[rowIndex] ?? 0
                  if (value <= 0) {
                    return null
                  }
                  const color = colors[segmentIndex]
                  const label = formatShareLabel(value)
                  return (
                    <div
                      key={segment.label}
                      title={`${segment.label} ${label}`}
                      className="flex h-full min-w-0 items-center justify-center overflow-hidden"
                      style={{
                        width: `${value}%`,
                        backgroundColor: color,
                        color: getChartInsideLabelColor(color),
                      }}
                    >
                      {value > INSIDE_LABEL_MIN ? (
                        <span
                          data-testid={`lane-media-pct-${row.id}-${segmentIndex}`}
                          className="pointer-events-none text-[10px] font-medium leading-none tabular-nums"
                        >
                          {label}
                        </span>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
          <span aria-hidden="true" />
          <div className="flex justify-between text-[10px] tabular-nums text-muted-foreground">
            {AXIS.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {segments.map((segment, segmentIndex) => (
            <span key={segment.label} className="inline-flex items-center gap-1.5 text-xs text-foreground">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: colors[segmentIndex] }}
              />
              {segment.label}
            </span>
          ))}
        </div>
      </div>
    </TooltipProvider>
  )
}
